import React from "react";

import { faCircleDot, faCircle } from "@fortawesome/pro-solid-svg-icons";
import { Tooltip } from "@material-ui/core";

import { SmallFaIcon } from "components/FaIcon";
import { Row } from "components/Layout";
import { Text } from "components/Text";

import { makeCommaSeparatedString, pluralize } from "lib/pluralize";

import { MissingFieldsIndicatorTypes } from "constants/saleLots";

import {
  useHasMLAOrDataPermission,
  useHasNASPermissions,
  useIsMobile,
} from "hooks";
import { MissingFieldsIndicatorBaseProps } from "./types";

export const defaultNutrienWarnings = ["Dentition", "Weight Range"];

// Display name mappings for MLA/Data indicator fields
const fieldDisplayNames: Record<string, string> = {
  "Estimated Average Mass Grams": "Est Live Weight",
  "Product Id": "Product",
  "Age Id": "Age",
  "Breed Id": "Breed",
  "Sex Id": "Sex",
};

export function MissingFieldsIndicatorBase(
  props: MissingFieldsIndicatorBaseProps,
): React.JSX.Element {
  const {
    warnings,
    label,
    overrideNoMissingField = false,
    includeLabel = true,
    areFieldsUnderWarningThreshold = false,
  } = props;
  // We can use the override when displaying warnings on a single field
  // For example, check if the warning array contains our field,
  // if it doesn't then we can overide with a green status

  const hasNASPermissions = useHasNASPermissions();
  const hasMLAOrDataPermission = useHasMLAOrDataPermission();

  const isMobile = useIsMobile();
  const isMobileAndNoLabel = isMobile && !includeLabel;

  if (
    (label === MissingFieldsIndicatorTypes.NAS && !hasNASPermissions) ||
    ((label === MissingFieldsIndicatorTypes.MLA ||
      label === MissingFieldsIndicatorTypes.DATA) &&
      !hasMLAOrDataPermission)
  ) {
    return null;
  }

  if (overrideNoMissingField) {
    warnings.length = 0;
  }

  let tooltipText = "";
  let color = "success";

  // Format warnings for display by looking up display names
  // If a field isn't in the map, fall back to removing "Id" suffix
  const formattedWarnings = warnings.map(
    item => fieldDisplayNames[item] || item.replace(/\bId\b$/i, ""),
  );

  if (warnings.length > 0) {
    // Show missing fields for both MLA and Data indicators
    const missingFieldsText = `The Required ${label} ${pluralize("Field", warnings.length)}: ${makeCommaSeparatedString(formattedWarnings)} ${warnings.length > 1 ? "are" : "is"} missing`;

    // For Data indicators, add recommendation message first, then missing fields
    if (label === MissingFieldsIndicatorTypes.DATA) {
      const formattedWarningsText = makeCommaSeparatedString(formattedWarnings);
      tooltipText = `In order to unlock improved data reporting and analytics, the ${formattedWarningsText} ${pluralize("field", warnings.length)} ${warnings.length === 1 ? "needs" : "need"} to be completed.`;
    } else {
      tooltipText = missingFieldsText;
    }
    color = "warning";
  } else if (areFieldsUnderWarningThreshold) {
    tooltipText = "Lot Validation Thresholds Not Met, Fields Not Required";
    color = "inactive";
  } else if (!overrideNoMissingField) {
    tooltipText = `All Required ${label} Fields Entered`;
  }

  return (
    <Row
      gridGap={1}
      padding={isMobileAndNoLabel ? 0 : 1}
      alignCenter
      alignStart={isMobileAndNoLabel}
      marginRight={0}
    >
      <Text fontSize="beta" color="titleLight">
        {includeLabel ? label : ""}
      </Text>
      <Tooltip title={tooltipText}>
        <SmallFaIcon
          icon={warnings.length > 0 ? faCircleDot : faCircle}
          color={color}
        />
      </Tooltip>
    </Row>
  );
}
